// @ts-check
// Seção "Favoritos" da gaveta (doc 07, seções 4.5 e 4.7; contrato de paridade, seção 4; contrato 08 de navegação,
// seção 2.3): views e registros marcados pelo membro e pastas dele. Cada favorito tem um menu para ir a uma pasta,
// sair dela ou deixar de ser favorito; cada pasta abre e fecha, e pode ser renomeada ou removida (os favoritos ficam).
import { html } from 'htm/preact';
import { useRef, useState } from 'preact/hooks';
import { locationState } from '../../app/router.js';
import { favoritesState } from '../../data/metadata-store.js';
import { removeFavorite } from '../../data/parity.js';
import { createNavigationItem, deleteNavigationItem, updateNavigationItem } from '../../data/navigation.js';
import { readStorage, writeStorage } from '../../lib/utils.js';
import { Icon } from '../../ui/icon.js';
import { LightIconButton } from '../../ui/button.js';
import { Dropdown, DropdownMenuItems, DropdownMenuSeparator, MenuItem } from '../../ui/dropdown.js';
import { Modal } from '../../ui/modal.js';
import { showErrorSnackbar } from '../../ui/snackbar.js';
import { t } from '../../i18n/index.js';

const OPEN_FOLDERS_STORAGE_KEY = 'appcrm.openFavoriteFolders';

/** @returns {Set<string>} */
function readOpenFolders() {
  try {
    const stored = JSON.parse(readStorage(OPEN_FOLDERS_STORAGE_KEY, '[]') ?? '[]');
    return new Set(Array.isArray(stored) ? stored : []);
  } catch {
    return new Set();
  }
}

/**
 * Nome de pasta: nova (sem folderId) ou renomear.
 * @param {{ initialName?: string, title: string, onClose: () => void, onConfirm: (name: string) => void }} props
 */
function FolderNameModal({ initialName = '', title, onClose, onConfirm }) {
  const [name, setName] = useState(initialName);
  return html`<${Modal} title=${title} confirmLabel=${t('common.save')} isConfirmDisabled=${!name.trim()} onClose=${onClose} onConfirm=${() => onConfirm(name.trim())}>
    <label class="navigation-edit-field">
      <span>${t('navigationMobile.favorites.folderName')}</span>
      <input class="text-input" value=${name} maxLength=${60} autofocus onInput=${(/** @type {any} */ event) => setName(event.currentTarget.value)} />
    </label>
  <//>`;
}

/**
 * Menu "..." de um favorito: pastas para onde ir, sair da pasta, remover dos favoritos.
 * @param {{ favorite: import('../../data/metadata-store.js').FavoriteEntry, folders: import('../../data/metadata-store.js').FavoriteEntry[] }} props
 */
function FavoriteOptions({ favorite, folders }) {
  const anchorRef = useRef(/** @type {HTMLSpanElement | null} */ (null));
  const [isOpen, setIsOpen] = useState(false);
  /** @param {() => Promise<unknown>} action */
  const run = (action) => {
    setIsOpen(false);
    action().catch(showErrorSnackbar);
  };
  return html`<span class="favorite-navigation-options" ref=${anchorRef}>
    <${LightIconButton} icon="IconDotsVertical" ariaLabel=${t('navigationMobile.favorites.options')} onClick=${() => setIsOpen(!isOpen)} />
    <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${() => setIsOpen(false)} width=${220}>
      <${DropdownMenuItems}>
        ${folders.filter((folder) => folder.id !== favorite.folderId).map((folder) => html`<${MenuItem}
          key=${folder.id}
          icon="IconFolderSymlink"
          text=${t('navigationMobile.favorites.moveTo', { name: folder.label })}
          onClick=${() => run(() => updateNavigationItem(favorite.id, { folderId: folder.id }))}
        />`)}
        ${favorite.folderId
          ? html`<${MenuItem} icon="IconArrowUp" text=${t('navigationMobile.favorites.removeFromFolder')} onClick=${() => run(() => updateNavigationItem(favorite.id, { folderId: null }))} />`
          : null}
        ${folders.length > 0 || favorite.folderId ? html`<${DropdownMenuSeparator} />` : null}
        <${MenuItem} icon="IconHeartOff" text=${t('parity.favorites.remove')} onClick=${() => run(() => removeFavorite(favorite.id))} />
      <//>
    <//>
  </span>`;
}

/**
 * Recebe os componentes da gaveta para manter o mesmo visual sem importar a gaveta (evita ciclo de módulos).
 * @param {{ NavigationSection: any, NavigationItem: any }} props
 */
export function FavoritesNavigationSection({ NavigationSection, NavigationItem }) {
  const [openFolders, setOpenFolders] = useState(readOpenFolders);
  const [folderModal, setFolderModal] = useState(/** @type {{ folderId: string | null, name: string } | null} */ (null));
  const favorites = favoritesState.value;
  const location = locationState.value;
  const current = `${location.pathname}${location.search}`;
  const folders = favorites.filter((favorite) => favorite.type === 'FOLDER');
  const folderIds = new Set(folders.map((folder) => folder.id));
  // favorito numa pasta que não existe mais fica solto
  const looseFavorites = favorites.filter((favorite) => favorite.type !== 'FOLDER' && (!favorite.folderId || !folderIds.has(favorite.folderId)));
  if (favorites.length === 0) return null;

  /** @param {string} folderId */
  const toggleFolder = (folderId) => {
    const next = new Set(openFolders);
    if (next.has(folderId)) next.delete(folderId);
    else next.add(folderId);
    setOpenFolders(next);
    writeStorage(OPEN_FOLDERS_STORAGE_KEY, JSON.stringify([...next]));
  };

  /** @param {import('../../data/metadata-store.js').FavoriteEntry} favorite @param {boolean} [isNested] */
  const renderFavorite = (favorite, isNested = false) => html`<div class="favorite-navigation-item" data-nested=${isNested ? 'true' : undefined} key=${favorite.id}>
    <${NavigationItem} label=${favorite.label} icon=${favorite.icon} href=${favorite.href} isActive=${current === favorite.href} />
    <${FavoriteOptions} favorite=${favorite} folders=${folders} />
  </div>`;

  return html`<${NavigationSection}
    id="Favorites"
    title=${t('parity.favorites.title')}
    rightAction=${html`<${LightIconButton} icon="IconFolderPlus" ariaLabel=${t('navigationMobile.favorites.newFolder')} onClick=${() => setFolderModal({ folderId: null, name: '' })} />`}
  >
    ${folders.map((folder) => {
      const children = favorites.filter((favorite) => favorite.type !== 'FOLDER' && favorite.folderId === folder.id);
      const isOpen = openFolders.has(folder.id);
      const containsActive = children.some((child) => child.href === current);
      return html`<div class="favorite-folder" key=${folder.id}>
        <div class="favorite-navigation-item">
          <button
            type="button"
            class="navigation-item"
            data-active=${!isOpen && containsActive ? 'true' : undefined}
            aria-expanded=${isOpen ? 'true' : 'false'}
            onClick=${() => toggleFolder(folder.id)}
          >
            <span class="navigation-item-icon"><${Icon} name=${folder.icon} size=${16} /></span>
            <span class="navigation-item-label">${folder.label}</span>
            <span class="favorite-folder-count">${children.length}</span>
            <${Icon} name=${isOpen ? 'IconChevronDown' : 'IconChevronRight'} size=${14} className="favorite-folder-chevron" />
          </button>
          <${FolderOptions} folder=${folder} onRename=${() => setFolderModal({ folderId: folder.id, name: folder.label })} />
        </div>
        ${isOpen ? html`<div class="favorite-folder-children">${children.length > 0
          ? children.map((child) => renderFavorite(child, true))
          : html`<div class="favorite-folder-empty">${t('navigationMobile.favorites.emptyFolder')}</div>`}</div>` : null}
      </div>`;
    })}
    ${looseFavorites.map((favorite) => renderFavorite(favorite))}
    ${folderModal
      ? html`<${FolderNameModal}
          title=${folderModal.folderId ? t('navigationMobile.favorites.renameFolder') : t('navigationMobile.favorites.newFolder')}
          initialName=${folderModal.name}
          onClose=${() => setFolderModal(null)}
          onConfirm=${(/** @type {string} */ name) => {
            const { folderId } = folderModal;
            setFolderModal(null);
            const action = folderId ? updateNavigationItem(folderId, { name }) : createNavigationItem({ type: 'FOLDER', name });
            action.then((item) => {
              if (!folderId && item?.id) toggleFolder(item.id);
            }).catch(showErrorSnackbar);
          }}
        />`
      : null}
  <//>`;
}

/**
 * @param {{ folder: import('../../data/metadata-store.js').FavoriteEntry, onRename: () => void }} props
 */
function FolderOptions({ folder, onRename }) {
  const anchorRef = useRef(/** @type {HTMLSpanElement | null} */ (null));
  const [isOpen, setIsOpen] = useState(false);
  return html`<span class="favorite-navigation-options" ref=${anchorRef}>
    <${LightIconButton} icon="IconDotsVertical" ariaLabel=${t('navigationMobile.favorites.folderOptions')} onClick=${() => setIsOpen(!isOpen)} />
    <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${() => setIsOpen(false)} width=${200}>
      <${DropdownMenuItems}>
        <${MenuItem} icon="IconPencil" text=${t('navigationMobile.favorites.renameFolder')} onClick=${() => {
          setIsOpen(false);
          onRename();
        }} />
        <${MenuItem} icon="IconTrash" accent="danger" text=${t('navigationMobile.favorites.deleteFolder')} onClick=${() => {
          setIsOpen(false);
          deleteNavigationItem(folder.id).catch(showErrorSnackbar);
        }} />
      <//>
    <//>
  </span>`;
}
